import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type DailyUpdateFormValues = {
  date: string;
  url: string;
};

export function validateDailyUpdateFormValues(
  values: DailyUpdateFormValues
): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(values.date)) {
    return "Date must be in YYYY-MM-DD format.";
  }

  if (!values.url.trim()) {
    return "Please enter a YouTube URL.";
  }

  return undefined;
}

type DailyUpdateFormProps = {
  initialValues: DailyUpdateFormValues;
  submitLabel: string;
  submittingLabel: string;
  isSubmitting: boolean;
  onSubmit: (values: DailyUpdateFormValues) => void;
  onCancel: () => void;
};

export function DailyUpdateForm({
  initialValues,
  submitLabel,
  submittingLabel,
  isSubmitting,
  onSubmit,
  onCancel,
}: DailyUpdateFormProps) {
  const [date, setDate] = useState<string>(initialValues.date);
  const [url, setUrl] = useState<string>(initialValues.url);

  useEffect(() => {
    setDate(initialValues.date);
    setUrl(initialValues.url);
  }, [initialValues.date, initialValues.url]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({ date, url });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="daily-update-modal-date">Date</Label>
        <Input
          id="daily-update-modal-date"
          type="date"
          value={date}
          disabled={isSubmitting}
          onChange={(event) => setDate(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="daily-update-modal-url">YouTube URL</Label>
        <Input
          id="daily-update-modal-url"
          type="url"
          placeholder="https://www.youtube.com/shorts/..."
          value={url}
          disabled={isSubmitting}
          onChange={(event) => setUrl(event.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || !url.trim()}>
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
